import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IMaskInput } from "react-imask";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Button,
  Badge,
  Accordion,
  ProgressBar,
} from "react-bootstrap";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { Save, CornerDownLeft, CheckCircle2, CircleDot } from "lucide-react";

import AlertMessage from "../../components/Common/AlertMessage";
import extractApiErrors from "../../utils/extractApiErrors";

import SelectCategory from "../../components/Common/SelectCategory";
import SelectCountry from "../../components/Common/SelectCountry";
import SelectDepartment from "../../components/Common/SelectDepartment";
import SelectMunicipality from "../../components/Common/SelectMunicipality";

import {
  createCustomer,
  updateCustomer,
  getCustomerById,
} from "../../services/customerService";

// ------------------------------------- UI: Sticky Header -------------------------------------
const StickyHeader = ({ title, onBack, onSave, disabled, progress }) => (
  <div className="bg-body border-bottom position-sticky top-0 z-3">
    <Container className="py-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="min-w-0">
          <h3 className="mb-0 text-truncate">{title}</h3>
          <div className="text-muted small mt-1">
            Completa los campos requeridos y guarda los cambios.
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="secondary"
            onClick={onBack}
            className="d-flex align-items-center gap-2"
            type="button"
          >
            <CornerDownLeft size={16} /> Volver
          </Button>
          <Button
            onClick={onSave}
            disabled={disabled}
            className="d-flex align-items-center gap-2"
            type="button"
          >
            <Save size={16} /> Guardar
          </Button>
        </div>
      </div>
      <div className="mt-3">
        <ProgressBar now={progress} style={{ height: 6 }} />
      </div>
    </Container>
  </div>
);

// ------------------------------------- Helpers -------------------------------------
const norm = (v) => (v || "").toString().trim().toUpperCase();
const isCustomerTypeLegalByCode = (code) => norm(code) === "JURIDICO";

const calculateNitDv = (nit) => {
  const clean = (nit || "").replace(/\D/g, "");
  if (!clean) return "";

  const weights = [71, 67, 59, 53, 47, 43, 41, 37, 29, 23, 19, 17, 13, 7, 3];

  let sum = 0;
  const reversed = clean.split("").reverse();
  for (let i = 0; i < reversed.length; i++) {
    const digit = parseInt(reversed[i], 10);
    sum += digit * (weights[i] || 0);
  }

  const mod = sum % 11;
  return mod > 1 ? String(11 - mod) : String(mod);
};

/**
 * Construye un schema Yup sin depender de `this.options.context` (evita crashes).
 * - Recibe `nitDocTypeId` y la función `isLegalEntityFn(values)` para reglas dinámicas.
 */
const buildValidationSchema = ({ nitDocTypeId, isLegalEntityFn }) =>
  Yup.object().shape({
    customerTypeId: Yup.string()
      .uuid("Tipo de cliente inválido.")
      .required("El tipo de cliente es obligatorio."),

    documentTypeId: Yup.string()
      .uuid("Tipo de documento inválido.")
      .required("El tipo de documento es obligatorio.")
      .when("customerTypeId", {
        is: (customerTypeId) => isLegalEntityFn({ customerTypeId }),
        then: (schema) =>
          nitDocTypeId
            ? schema.oneOf(
                [nitDocTypeId],
                "Para cliente jurídico, el tipo de documento debe ser NIT."
              )
            : schema, // si aún no sabemos el GUID de NIT, no bloqueamos
      }),

    documentNumber: Yup.string()
      .required("El número de documento es obligatorio.")
      .max(50, "Máximo 50 caracteres.")
      .matches(/^\d+$/, "Debe contener solo números.")
      .test("trim", "Sin espacios al inicio/fin.", (v) =>
        typeof v === "string" ? v === v.trim() : true
      )
      .when(["customerTypeId", "documentTypeId"], {
        is: (customerTypeId, documentTypeId) => {
          const isLegal = isLegalEntityFn({ customerTypeId });
          const isNit = nitDocTypeId ? documentTypeId === nitDocTypeId : false;
          return isLegal || isNit;
        },
        then: (s) =>
          s
            .min(7, "El NIT debe tener al menos 7 dígitos.")
            .max(10, "El NIT debe tener máximo 10 dígitos.")
            .required("El NIT es obligatorio."),
      }),

    firstName: Yup.string()
      .nullable()
      .max(100, "Máximo 100 caracteres.")
      .matches(/^[\p{L}\p{M}'\- ]+$/u, "Solo letras y espacios.")
      .test(
        "trim",
        "Sin espacios al inicio/fin.",
        (v) => v == null || v === v.trim()
      )
      .when("customerTypeId", {
        is: (customerTypeId) => !isLegalEntityFn({ customerTypeId }),
        then: (s) => s.required("El nombre es obligatorio."),
        otherwise: (s) => s.nullable(),
      }),

    lastName: Yup.string()
      .nullable()
      .max(100, "Máximo 100 caracteres.")
      .matches(/^[\p{L}\p{M}'\- ]+$/u, "Solo letras y espacios.")
      .test(
        "trim",
        "Sin espacios al inicio/fin.",
        (v) => v == null || v === v.trim()
      )
      .when("customerTypeId", {
        is: (customerTypeId) => !isLegalEntityFn({ customerTypeId }),
        then: (s) => s.required("El apellido es obligatorio."),
        otherwise: (s) => s.nullable(),
      }),

    birthDate: Yup.date()
      .nullable()
      .max(new Date(), "Debe ser una fecha pasada."),
    genderId: Yup.string().nullable().uuid("Género inválido."),

    phoneMobile: Yup.string()
      .required("El número de celular es obligatorio.")
      .matches(/^3\d{9}$/, "Debe iniciar en 3 y tener 10 dígitos.")
      .trim(),

    phoneAlt: Yup.string()
      .nullable()
      .max(30, "Máximo 30 caracteres.")
      .test(
        "optional",
        "Debe tener entre 7 y 15 dígitos (con o sin +).",
        (value) => !value || value.trim() === "" || /^\+?\d{7,15}$/.test(value)
      ),

    email: Yup.string().nullable().trim().email("Correo inválido."),

    addressLine1: Yup.string()
      .required("La dirección principal es obligatoria.")
      .max(250, "Máximo 250 caracteres.")
      .test(
        "co-address-balanced",
        "Dirección inválida. Ej: 'Calle 103EE # 65-63' (puedes agregar Apto/Torre/Int).",
        (value) => {
          const v = (value || "").trim();
          if (!v) return false;

          // Normaliza espacios
          const s = v.replace(/\s+/g, " ");

          // 1) Debe iniciar por tipo de vía
          const roadOk =
            /^(Calle|Cra|Carrera|Transversal|Diagonal|Circular|Av|Avenida|Autopista)\b/i.test(
              s
            );
          if (!roadOk) return false;

          // 2) Debe tener el bloque principal: <vía> <número+letras/bis/sur...> # <número+letras> - <número+letras>
          // - acepta letras 0..3: 103EE, 80B, 12A
          // - acepta BIS opcional
          // - acepta Sur/Norte/Este/Oeste opcional
          const mainPattern =
            /^(Calle|Cra|Carrera|Transversal|Diagonal|Circular|Av|Avenida|Autopista)\s+\d+[A-Z]{0,3}(?:\s+BIS)?(?:\s+(Sur|Norte|Este|Oeste))?\s*(#|No\.?|N°)\s*\d+[A-Z]{0,3}\s*-\s*\d+[A-Z]{0,3}(?:\s*(?:Apto|Apartamento|Torre|Int|Interior|Casa|Local|Oficina)\s*[\w-]+)?$/i;

          if (!mainPattern.test(s)) return false;

          // 3) Seguridad UX: evita caracteres raros (solo letras/números/espacios y signos comunes)
          //    Compatible con builds que NO soportan \p{L}/\p{M}
          const safeChars = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9\s#\-\.\u00B0]+$/.test(s);
          if (!safeChars) return false;

          // 4) Evita duplicidades raras
          if (/(##|--)/.test(s)) return false;

          return true;
        }
      ),

    addressLine2: Yup.string().nullable().max(250, "Máximo 250 caracteres."),

    countryId: Yup.string()
      .uuid("País inválido.")
      .required("El país es obligatorio."),
    departmentId: Yup.string()
      .uuid("Departamento inválido.")
      .required("El departamento es obligatorio."),
    municipalityId: Yup.string()
      .uuid("Municipio inválido.")
      .required("El municipio es obligatorio."),

    companyName: Yup.string()
      .nullable()
      .max(200, "Máximo 200 caracteres.")
      .when("customerTypeId", {
        is: (customerTypeId) => isLegalEntityFn({ customerTypeId }),
        then: (s) =>
          s.required("La razón social es obligatoria para persona jurídica."),
      }),

    taxVerificationDigit: Yup.string()
      .nullable()
      .matches(/^\d{1}$/, "El DV debe ser 1 dígito.")
      .when("customerTypeId", {
        is: (customerTypeId) => isLegalEntityFn({ customerTypeId }),
        then: (s) => s.required("El DV es obligatorio."),
      }),

    taxId: Yup.string().nullable().max(30, "Máximo 30 caracteres."),
    businessActivity: Yup.string()
      .nullable()
      .max(150, "Máximo 150 caracteres."),

    notes: Yup.string().nullable().max(1000, "Máximo 1000 caracteres."),
    isActive: Yup.boolean().default(true),
  });

// ------------------------------------- Iniciales -------------------------------------
const initialValuesBase = {
  customerTypeId: "",
  documentTypeId: "",
  documentNumber: "",
  firstName: "",
  lastName: "",
  birthDate: "",
  genderId: "",
  phoneMobile: "",
  phoneAlt: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  countryId: "",
  departmentId: "",
  municipalityId: "",
  companyName: "",
  taxId: "",
  taxVerificationDigit: "",
  businessActivity: "",
  notes: "",
  isActive: true,
};

const SECTION_LIST = [
  { key: "tipo", label: "Tipo de cliente" },
  { key: "identidad", label: "Identidad y datos básicos" },
  { key: "contacto", label: "Contacto" },
  { key: "ubicacion", label: "Ubicación" },
  { key: "empresa", label: "Información comercial / fiscal" },
  { key: "notas", label: "Notas" },
];

// ------------------------------------- Page -------------------------------------
const CustomerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isCreate = !id;

  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  const sectionRefs = useRef(
    SECTION_LIST.reduce(
      (acc, s) => ({ ...acc, [s.key]: React.createRef() }),
      {}
    )
  );

  const [openKeys, setOpenKeys] = useState(["tipo", "identidad"]);
  const toggleKey = (k) =>
    setOpenKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  const openAndScroll = (k) => {
    setOpenKeys((prev) => (prev.includes(k) ? prev : [...prev, k]));
    const el = sectionRefs.current[k]?.current;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const [customerTypeItem, setCustomerTypeItem] = useState(null);
  const [documentTypeItem, setDocumentTypeItem] = useState(null);
  const [nitDocTypeItem, setNitDocTypeItem] = useState(null);

  const [dvManuallyEdited, setDvManuallyEdited] = useState(false);

  const isLegalEntity = useMemo(
    () => isCustomerTypeLegalByCode(customerTypeItem?.code),
    [customerTypeItem]
  );

  const isNitDocType = useMemo(
    () => norm(documentTypeItem?.code) === "NIT",
    [documentTypeItem]
  );

  // Schema dinámico (evita el error de `this.options`)
  const schema = useMemo(() => {
    const isLegalEntityFn = ({ customerTypeId }) => {
      // Nota: customerTypeId es GUID; aquí la “verdad” la determina customerTypeItem
      // Pero para Yup, al cambiar customerTypeId aún no sabemos raw; por eso validamos
      // la obligatoriedad del NIT principalmente vía auto-selección + oneOf con nitDocTypeId.
      // Para nombres/apellidos sí usamos customerTypeId como indicador de cambio,
      // y lo reforzamos con limpieza de campos en useEffect.
      return isLegalEntity;
    };

    return buildValidationSchema({
      nitDocTypeId: nitDocTypeItem?.id || null,
      isLegalEntityFn,
    });
  }, [nitDocTypeItem, isLegalEntity]);

  const formik = useFormik({
    initialValues: initialValuesBase,
    validationSchema: schema,
    validateOnBlur: true,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setErrors([]);
      setSuccessMessage(null);

      try {
        const payload = {
          id: isCreate ? undefined : id,

          customerTypeId: values.customerTypeId,

          documentTypeId: values.documentTypeId,
          documentNumber: values.documentNumber,

          firstName: isLegalEntity ? null : values.firstName || null,
          lastName: isLegalEntity ? null : values.lastName || null,
          birthDate: isLegalEntity ? null : values.birthDate || null,
          genderId: isLegalEntity ? null : values.genderId || null,

          phoneMobile: values.phoneMobile,
          phoneAlt: values.phoneAlt || null,
          email: values.email || null,

          addressLine1: values.addressLine1,
          addressLine2: values.addressLine2 || null,
          countryId: values.countryId,
          departmentId: values.departmentId,
          municipalityId: values.municipalityId,

          companyName: isLegalEntity ? values.companyName || null : null,
          taxId: isLegalEntity ? values.documentNumber : null,
          taxVerificationDigit: isLegalEntity
            ? values.taxVerificationDigit || null
            : null,
          businessActivity: isLegalEntity
            ? values.businessActivity || null
            : null,

          notes: values.notes || null,
          isActive: values.isActive ?? true,
        };

        let resp;
        if (isCreate) resp = await createCustomer(payload);
        else resp = await updateCustomer(id, payload);

        if (!resp?.success) throw { backend: resp };

        setSuccessMessage(
          isCreate
            ? "✅ Cliente creado correctamente."
            : "✅ Cliente actualizado correctamente."
        );

        Swal.fire({
          toast: true,
          position: "top",
          icon: "success",
          title: isCreate ? "Cliente creado" : "Cambios guardados",
          timer: 1800,
          showConfirmButton: false,
        });

        setTimeout(() => navigate("/dashboard/customers"), 1000);
      } catch (err) {
        setErrors(extractApiErrors(err));
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Limpieza en cascada: País -> limpia Departamento y Municipio
  useEffect(() => {
    // Si el país cambia, reiniciamos dependencias
    formik.setFieldValue("departmentId", "", false);
    formik.setFieldValue("municipalityId", "", false);

    // Opcional: también marcar como no tocado para evitar errores visibles inmediatos
    formik.setFieldTouched("departmentId", false, false);
    formik.setFieldTouched("municipalityId", false, false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.countryId]);

  // Limpieza en cascada: Departamento -> limpia Municipio
  useEffect(() => {
    formik.setFieldValue("municipalityId", "", false);
    formik.setFieldTouched("municipalityId", false, false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.departmentId]);

  // Jurídico => auto-seleccionar NIT como tipo de documento
  useEffect(() => {
    if (!isLegalEntity) return;
    if (!nitDocTypeItem?.id) return;

    if (formik.values.documentTypeId !== nitDocTypeItem.id) {
      formik.setFieldValue("documentTypeId", nitDocTypeItem.id, false);
      setDocumentTypeItem(nitDocTypeItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLegalEntity, nitDocTypeItem?.id]);

  // Limpieza de campos irrelevantes
  useEffect(() => {
    if (isLegalEntity) {
      formik.setFieldValue("firstName", "", false);
      formik.setFieldValue("lastName", "", false);
      formik.setFieldValue("birthDate", "", false);
      formik.setFieldValue("genderId", "", false);
      setDvManuallyEdited(false);
    } else {
      formik.setFieldValue("companyName", "", false);
      formik.setFieldValue("taxId", "", false);
      formik.setFieldValue("taxVerificationDigit", "", false);
      formik.setFieldValue("businessActivity", "", false);
      setDvManuallyEdited(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLegalEntity]);

  // Auto-DV (solo jurídico y si no lo editó manualmente)
  useEffect(() => {
    if (!isLegalEntity) return;
    if (dvManuallyEdited) return;

    const nit = (formik.values.documentNumber || "").replace(/\D/g, "");
    if (nit.length < 7) return;

    const dv = calculateNitDv(nit);
    if (dv && formik.values.taxVerificationDigit !== dv) {
      formik.setFieldValue("taxVerificationDigit", dv, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLegalEntity, dvManuallyEdited, formik.values.documentNumber]);

  // Cargar para edición
  useEffect(() => {
    if (isCreate) return;

    (async () => {
      try {
        const r = await getCustomerById(id);
        if (!r?.success) throw new Error("No se pudo cargar el cliente.");
        const d = r.data;

        const docNumber = d.taxId || d.documentNumber || "";

        formik.setValues({
          ...initialValuesBase,
          customerTypeId: d.customerTypeId || "",
          documentTypeId: d.documentTypeId || "",
          documentNumber: docNumber,
          firstName: d.firstName || "",
          lastName: d.lastName || "",
          birthDate: d.birthDate ? d.birthDate.substring(0, 10) : "",
          genderId: d.genderId || "",
          phoneMobile: d.phoneMobile || "",
          phoneAlt: d.phoneAlt || "",
          email: d.email || "",
          addressLine1: d.addressLine1 || "",
          addressLine2: d.addressLine2 || "",
          countryId: d.countryId || "",
          departmentId: d.departmentId || "",
          municipalityId: d.municipalityId || "",
          companyName: d.companyName || "",
          taxId: d.taxId || "",
          taxVerificationDigit: d.taxVerificationDigit || "",
          businessActivity: d.businessActivity || "",
          notes: d.notes || "",
          isActive: d.isActive ?? true,
        });

        if (d.taxVerificationDigit) setDvManuallyEdited(true);
      } catch {
        Swal.fire("Error", "No se pudo cargar el cliente.", "error");
        navigate("/dashboard/customers");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isCreate]);

  // Hotkeys
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        formik.handleSubmit();
      }
      if (e.key === "Escape") navigate("/dashboard/customers");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [formik, navigate]);

  // Progreso
  const isFilled = (v) => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim() !== "";
    return v !== null && v !== undefined && v !== "";
  };

  const requiredBySection = useMemo(() => {
    if (isLegalEntity) {
      return {
        tipo: ["customerTypeId"],
        identidad: ["documentTypeId", "documentNumber", "taxVerificationDigit"],
        contacto: ["phoneMobile"],
        ubicacion: [
          "addressLine1",
          "countryId",
          "departmentId",
          "municipalityId",
        ],
        empresa: ["companyName"],
        notas: [],
      };
    }

    return {
      tipo: ["customerTypeId"],
      identidad: ["documentTypeId", "documentNumber", "firstName", "lastName"],
      contacto: ["phoneMobile"],
      ubicacion: [
        "addressLine1",
        "countryId",
        "departmentId",
        "municipalityId",
      ],
      empresa: [],
      notas: [],
    };
  }, [isLegalEntity]);

  const sectionIsComplete = (key) =>
    (requiredBySection[key] || []).every((path) => {
      const v = path.split(".").reduce((acc, p) => acc?.[p], formik.values);
      return isFilled(v);
    });

  const allRequired = Object.keys(requiredBySection).flatMap(
    (k) => requiredBySection[k]
  );
  const pendingCount = allRequired.filter((path) => {
    const v = path.split(".").reduce((acc, p) => acc?.[p], formik.values);
    return !isFilled(v);
  }).length;

  const progress = Math.round(
    (1 - pendingCount / (allRequired.length || 1)) * 100
  );

  const isInvalid = (name) => formik.touched[name] && !!formik.errors[name];
  const errorMsg = (name) => (formik.touched[name] ? formik.errors[name] : "");

  return (
    <>
      <StickyHeader
        title={isCreate ? "Crear cliente" : "Editar cliente"}
        onBack={() => navigate("/dashboard/customers")}
        onSave={formik.handleSubmit}
        disabled={formik.isSubmitting}
        progress={progress}
      />

      <div className="container-fluid mt-4 px-4 w-100">
        <Row className="g-4">
          {/* Sidebar */}
          <Col lg={3}>
            <Card className="shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="fw-semibold">Secciones</div>
                  <Badge bg={pendingCount ? "warning" : "success"}>
                    {pendingCount ? `${pendingCount} pendientes` : "Completo"}
                  </Badge>
                </div>

                <div className="list-group list-group-flush">
                  {SECTION_LIST.map((s) => {
                    const complete = sectionIsComplete(s.key);
                    const open = openKeys.includes(s.key);
                    return (
                      <button
                        key={s.key}
                        type="button"
                        className={`list-group-item list-group-item-action d-flex align-items-center justify-content-between ${
                          open ? "active" : ""
                        }`}
                        onClick={() => openAndScroll(s.key)}
                      >
                        <span className="text-start">{s.label}</span>
                        {complete ? (
                          <CheckCircle2 size={18} className="text-success" />
                        ) : (
                          <CircleDot
                            size={16}
                            className="text-danger"
                            style={{ opacity: 0.7 }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3 small text-muted">
                  {isLegalEntity ? (
                    <>
                      Cliente <strong>jurídico</strong>: Razón social + NIT +
                      DV.
                      <br />
                      El tipo de documento se fija automáticamente en{" "}
                      <strong>NIT</strong>.
                    </>
                  ) : (
                    <>
                      Cliente <strong>natural</strong>: Documento +
                      Nombres/Apellidos.
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Contenido principal */}
          <Col lg={9}>
            <Card className="shadow-sm">
              <Card.Body>
                <FormikProvider value={formik}>
                  <Form noValidate onSubmit={formik.handleSubmit}>
                    <Accordion
                      alwaysOpen
                      activeKey={openKeys}
                      onSelect={() => {}}
                    >
                      {/* TIPO */}
                      <Accordion.Item
                        eventKey="tipo"
                        ref={sectionRefs.current.tipo}
                      >
                        <Accordion.Header onClick={() => toggleKey("tipo")}>
                          Tipo de cliente{" "}
                          {sectionIsComplete("tipo") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>

                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="customerTypeId">
                                <Form.Label>Tipo *</Form.Label>
                                <SelectCategory
                                  categoryCode="customer_type"
                                  showCode={false}
                                  value={formik.values.customerTypeId}
                                  onChange={(val) =>
                                    formik.setFieldValue(
                                      "customerTypeId",
                                      val,
                                      true
                                    )
                                  }
                                  onSelectItem={(item) =>
                                    setCustomerTypeItem(item)
                                  }
                                  placeholder="Seleccione tipo de cliente"
                                />
                                {isInvalid("customerTypeId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("customerTypeId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="isActive">
                                <Form.Label>Estado</Form.Label>
                                <Form.Check
                                  type="switch"
                                  name="isActive"
                                  label={
                                    formik.values.isActive
                                      ? "Activo"
                                      : "Inactivo"
                                  }
                                  checked={formik.values.isActive}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      "isActive",
                                      e.target.checked
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* IDENTIDAD */}
                      <Accordion.Item
                        eventKey="identidad"
                        ref={sectionRefs.current.identidad}
                      >
                        <Accordion.Header
                          onClick={() => toggleKey("identidad")}
                        >
                          Identidad y datos básicos{" "}
                          {sectionIsComplete("identidad") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>

                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="documentTypeId">
                                <Form.Label>Tipo de documento *</Form.Label>
                                <SelectCategory
                                  categoryCode="document_type"
                                  showCode={false}
                                  value={formik.values.documentTypeId}
                                  onChange={(val) =>
                                    formik.setFieldValue(
                                      "documentTypeId",
                                      val,
                                      true
                                    )
                                  }
                                  onSelectItem={(item) =>
                                    setDocumentTypeItem(item)
                                  }
                                  onItemsLoaded={(items) => {
                                    const nit =
                                      items?.find(
                                        (x) => norm(x.code) === "NIT"
                                      ) || null;
                                    setNitDocTypeItem(nit);
                                  }}
                                  placeholder={
                                    isLegalEntity
                                      ? "NIT (obligatorio)"
                                      : "Seleccione tipo de documento"
                                  }
                                  isDisabled={isLegalEntity}
                                />
                                {isInvalid("documentTypeId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("documentTypeId")}
                                  </div>
                                )}
                                {isLegalEntity && (
                                  <Form.Text className="text-muted">
                                    Para cliente jurídico, el documento siempre
                                    es <strong>NIT</strong>.
                                  </Form.Text>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={isLegalEntity ? 4 : 6}>
                              <Form.Group controlId="documentNumber">
                                <Form.Label>
                                  {isLegalEntity
                                    ? "NIT *"
                                    : "Número de documento *"}
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={50}
                                  name="documentNumber"
                                  value={formik.values.documentNumber}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      "documentNumber",
                                      e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 50),
                                      true
                                    )
                                  }
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    formik.touched.documentNumber &&
                                    !!formik.errors.documentNumber
                                  }
                                  placeholder={
                                    isLegalEntity ? "Ej: 900123456" : ""
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.documentNumber}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            {isLegalEntity && (
                              <Col md={2}>
                                <Form.Group controlId="taxVerificationDigit">
                                  <Form.Label>DV *</Form.Label>
                                  <Form.Control
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    name="taxVerificationDigit"
                                    value={
                                      formik.values.taxVerificationDigit || ""
                                    }
                                    onChange={(e) => {
                                      setDvManuallyEdited(true);
                                      formik.setFieldValue(
                                        "taxVerificationDigit",
                                        e.target.value
                                          .replace(/\D/g, "")
                                          .slice(0, 1),
                                        true
                                      );
                                    }}
                                    onBlur={formik.handleBlur}
                                    isInvalid={isInvalid(
                                      "taxVerificationDigit"
                                    )}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errorMsg("taxVerificationDigit")}
                                  </Form.Control.Feedback>
                                  <Form.Text className="text-muted">
                                    Se calcula automáticamente.
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                            )}

                            {!isLegalEntity && (
                              <>
                                <Col md={6}>
                                  <Form.Group controlId="firstName">
                                    <Form.Label>Nombres *</Form.Label>
                                    <Form.Control
                                      type="text"
                                      name="firstName"
                                      value={formik.values.firstName}
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      isInvalid={isInvalid("firstName")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errorMsg("firstName")}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>

                                <Col md={6}>
                                  <Form.Group controlId="lastName">
                                    <Form.Label>Apellidos *</Form.Label>
                                    <Form.Control
                                      type="text"
                                      name="lastName"
                                      value={formik.values.lastName}
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      isInvalid={isInvalid("lastName")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errorMsg("lastName")}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>

                                <Col md={6}>
                                  <Form.Group controlId="birthDate">
                                    <Form.Label>Fecha de nacimiento</Form.Label>
                                    <Form.Control
                                      type="date"
                                      name="birthDate"
                                      value={formik.values.birthDate || ""}
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      isInvalid={isInvalid("birthDate")}
                                      max={
                                        new Date().toISOString().split("T")[0]
                                      }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errorMsg("birthDate")}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>

                                <Col md={6}>
                                  <Form.Group controlId="genderId">
                                    <Form.Label>Género</Form.Label>
                                    <SelectCategory
                                      categoryCode="gender"
                                      showCode={false}
                                      value={formik.values.genderId}
                                      onChange={(val) =>
                                        formik.setFieldValue(
                                          "genderId",
                                          val,
                                          true
                                        )
                                      }
                                      placeholder="Seleccione género"
                                    />
                                    {isInvalid("genderId") && (
                                      <div className="invalid-feedback d-block">
                                        {errorMsg("genderId")}
                                      </div>
                                    )}
                                  </Form.Group>
                                </Col>
                              </>
                            )}
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* CONTACTO */}
                      <Accordion.Item
                        eventKey="contacto"
                        ref={sectionRefs.current.contacto}
                      >
                        <Accordion.Header onClick={() => toggleKey("contacto")}>
                          Contacto{" "}
                          {sectionIsComplete("contacto") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>

                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="phoneMobile">
                                <Form.Label>Celular *</Form.Label>
                                <Form.Control
                                  as={IMaskInput}
                                  mask="3000000000"
                                  name="phoneMobile"
                                  value={formik.values.phoneMobile}
                                  onAccept={(value) =>
                                    formik.setFieldValue(
                                      "phoneMobile",
                                      value,
                                      true
                                    )
                                  }
                                  onBlur={formik.handleBlur}
                                  placeholder="3XXXXXXXXX"
                                  isInvalid={
                                    formik.touched.phoneMobile &&
                                    !!formik.errors.phoneMobile
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.phoneMobile}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="phoneAlt">
                                <Form.Label>Teléfono alternativo</Form.Label>
                                <Form.Control
                                  type="text"
                                  inputMode="tel"
                                  maxLength={15}
                                  name="phoneAlt"
                                  value={formik.values.phoneAlt || ""}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /[^\d+]/g,
                                      ""
                                    );
                                    formik.setFieldValue("phoneAlt", val, true);
                                  }}
                                  onBlur={formik.handleBlur}
                                  isInvalid={isInvalid("phoneAlt")}
                                  placeholder="+573001234567 o 6041234567"
                                />
                                <Form.Text className="text-muted">
                                  Entre 7 y 15 dígitos (puede incluir +).
                                </Form.Text>
                                <Form.Control.Feedback type="invalid">
                                  {errorMsg("phoneAlt")}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={12}>
                              <Form.Group controlId="email">
                                <Form.Label>Correo (contacto)</Form.Label>
                                <Form.Control
                                  type="email"
                                  name="email"
                                  value={formik.values.email}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={isInvalid("email")}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorMsg("email")}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* UBICACIÓN */}
                      <Accordion.Item
                        eventKey="ubicacion"
                        ref={sectionRefs.current.ubicacion}
                      >
                        <Accordion.Header
                          onClick={() => toggleKey("ubicacion")}
                        >
                          Ubicación{" "}
                          {sectionIsComplete("ubicacion") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>

                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="addressLine1">
                                <Form.Label>Dirección principal *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="addressLine1"
                                  value={formik.values.addressLine1}
                                  onChange={(e) => {
                                    const cleaned = e.target.value.replace(
                                      /\s+/g,
                                      " "
                                    );
                                    formik.setFieldValue(
                                      "addressLine1",
                                      cleaned,
                                      true
                                    );
                                  }}
                                  onBlur={formik.handleBlur}
                                  isInvalid={isInvalid("addressLine1")}
                                  placeholder="Calle 45 # 32-15"
                                  maxLength={250}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorMsg("addressLine1")}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="addressLine2">
                                <Form.Label>Complemento</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="addressLine2"
                                  value={formik.values.addressLine2}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={isInvalid("addressLine2")}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorMsg("addressLine2")}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group controlId="countryId">
                                <Form.Label>País *</Form.Label>
                                <SelectCountry
                                  value={formik.values.countryId}
                                  onChange={(val) => {
                                    formik.setFieldValue(
                                      "countryId",
                                      val,
                                      true
                                    );
                                    formik.setFieldValue(
                                      "departmentId",
                                      "",
                                      false
                                    );
                                    formik.setFieldValue(
                                      "municipalityId",
                                      "",
                                      false
                                    );
                                  }}
                                />
                                {isInvalid("countryId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("countryId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group controlId="departmentId">
                                <Form.Label>Departamento *</Form.Label>
                                <SelectDepartment
                                  countryId={formik.values.countryId}
                                  value={formik.values.departmentId}
                                  onChange={(val) => {
                                    formik.setFieldValue(
                                      "departmentId",
                                      val,
                                      true
                                    );
                                    formik.setFieldValue(
                                      "municipalityId",
                                      "",
                                      false
                                    );
                                  }}
                                  isDisabled={!formik.values.countryId}
                                />
                                {isInvalid("departmentId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("departmentId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group controlId="municipalityId">
                                <Form.Label>Municipio *</Form.Label>
                                <SelectMunicipality
                                  departmentId={formik.values.departmentId}
                                  value={formik.values.municipalityId}
                                  onChange={(val) =>
                                    formik.setFieldValue(
                                      "municipalityId",
                                      val,
                                      true
                                    )
                                  }
                                  isDisabled={!formik.values.departmentId}
                                />
                                {isInvalid("municipalityId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("municipalityId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* EMPRESA */}
                      <Accordion.Item
                        eventKey="empresa"
                        ref={sectionRefs.current.empresa}
                      >
                        <Accordion.Header onClick={() => toggleKey("empresa")}>
                          Información comercial / fiscal{" "}
                          {sectionIsComplete("empresa") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>

                        <Accordion.Body>
                          {!isLegalEntity ? (
                            <div className="text-muted">
                              Esta sección aplica principalmente para clientes{" "}
                              <strong>jurídicos</strong>.
                            </div>
                          ) : (
                            <Row className="g-3">
                              <Col md={12}>
                                <Form.Group controlId="companyName">
                                  <Form.Label>Razón social *</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="companyName"
                                    value={formik.values.companyName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    isInvalid={isInvalid("companyName")}
                                    placeholder="Ej: ACME S.A.S."
                                    maxLength={200}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errorMsg("companyName")}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>

                              <Col md={12}>
                                <Form.Group controlId="businessActivity">
                                  <Form.Label>Actividad económica</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="businessActivity"
                                    value={formik.values.businessActivity}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    isInvalid={isInvalid("businessActivity")}
                                    maxLength={150}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errorMsg("businessActivity")}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                            </Row>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* NOTAS */}
                      <Accordion.Item
                        eventKey="notas"
                        ref={sectionRefs.current.notas}
                      >
                        <Accordion.Header onClick={() => toggleKey("notas")}>
                          Notas
                        </Accordion.Header>
                        <Accordion.Body>
                          <Form.Group controlId="notes">
                            <Form.Label>Notas</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={5}
                              name="notes"
                              value={formik.values.notes}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              isInvalid={isInvalid("notes")}
                              placeholder="Observaciones generales del cliente…"
                              maxLength={1000}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errorMsg("notes")}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>

                    <div className="mt-3">
                      {errors.length > 0 && (
                        <AlertMessage
                          type="error"
                          message={
                            <ul className="mb-0">
                              {errors.map((e, i) => (
                                <li key={i}>{e}</li>
                              ))}
                            </ul>
                          }
                          onClose={() => setErrors([])}
                        />
                      )}

                      {successMessage && (
                        <AlertMessage
                          type="success"
                          message={successMessage}
                          onClose={() => setSuccessMessage(null)}
                        />
                      )}
                    </div>

                    <div className="d-flex justify-content-between mt-3">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/dashboard/customers")}
                      >
                        Volver
                      </button>

                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={formik.isSubmitting}
                      >
                        {formik.isSubmitting
                          ? "Guardando..."
                          : isCreate
                          ? "Crear"
                          : "Actualizar"}
                      </button>
                    </div>
                  </Form>
                </FormikProvider>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default CustomerForm;
